/**
 * Feb 2026 · User-Directive (3 Sep 2026) „Toast-Historie":
 * Kleine Aktivitaets-Kachel im Team-Dashboard, die alle bereits
 * weggedrueckten Angebots-Entscheidungen chronologisch (neueste zuerst)
 * anzeigt. Reine Read-Only-Ansicht — keine erneute Toast-Ausloesung,
 * keine Dismiss-Buttons. Klick auf eine Zeile fuehrt in /team/quotes.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, XCircle } from "lucide-react";
import api from "../lib/api";
import { BRAND_COLORS, STATUS_COLORS, withAlpha } from "../lib/bookingStatus";
import { deDateMedium, deTime } from "../lib/dateFormat";
import { deCurrency } from "../lib/numberFormat";

const HISTORY_LIMIT = 8;

function formatDate(iso) {
  if (!iso) return "";
  try {
    return deDateMedium(iso);
  } catch {
    return iso.split("T")[0] || iso;
  }
}

function formatTime(iso) {
  if (!iso) return "";
  try {
    return deTime(iso);
  } catch {
    return "";
  }
}

function money(n, cur = "EUR") {
  if (typeof n !== "number") return "";
  try {
    return deCurrency(n, cur);
  } catch {
    return `${n} ${cur}`;
  }
}

export default function QuoteDecisionHistoryCard() {
  const [rows, setRows] = useState(null); // null = loading

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/team/quote-events/history?limit=${HISTORY_LIMIT}`);
        if (!cancelled) setRows(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setRows([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Kachel wird komplett ausgeblendet, solange geladen wird oder gar keine
  // Historie existiert — spart Platz im Dashboard bei frisch eingerichteten
  // Instanzen. Sobald es die erste Entscheidung gibt, blendet sich der
  // Rueckblick automatisch ein.
  if (!rows) return null;
  if (rows.length === 0) return null;

  return (
    <div
      className="bg-white border border-navy/10 mt-8"
      data-testid="quote-decision-history-card"
    >
      <div className="p-6 border-b border-navy/10 flex items-center justify-between">
        <div>
          <div className="label-caps mb-1" style={{ color: BRAND_COLORS.gold }}>Aktivitaet</div>
          <h2 className="font-display text-2xl text-navy">Angebots-Entscheidungen</h2>
          <p className="text-[12px] text-navy-dark/60 mt-1">
            Bereits gelesene Kunden-Entscheidungen — Rueckblick auf die letzten {HISTORY_LIMIT} Ereignisse.
          </p>
        </div>
        <Link
          to="/team/quotes"
          className="text-[12px] tracking-[0.15em] uppercase text-navy border-b border-gold hover:text-gold"
        >
          Angebote &rarr;
        </Link>
      </div>
      <ul>
        {rows.map((ev) => {
          const approved = ev.decision === "approved";
          const color = approved ? STATUS_COLORS.greenDeep || "#3F9E5A" : STATUS_COLORS.red;
          const label = approved ? "Genehmigt" : "Abgelehnt";
          const Icon = approved ? CheckCircle2 : XCircle;
          const total = ev.quote?.total;
          return (
            <li
              key={ev.id}
              data-testid={`quote-history-row-${ev.id}`}
              data-decision={ev.decision}
              className="flex items-start gap-3 px-6 py-4 border-b border-navy/5 last:border-b-0"
            >
              <span
                className="mt-0.5 shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-full"
                style={{ background: withAlpha(color, 0.14) }}
                aria-hidden="true"
              >
                <Icon className="w-4 h-4" strokeWidth={1.8} style={{ color }} />
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span
                    className="text-[10px] tracking-[0.18em] uppercase font-medium"
                    style={{ color }}
                  >{label}</span>
                  {ev.quote?.number && (
                    <span className="text-[11px] tabular-nums text-navy-dark/60">{ev.quote.number}</span>
                  )}
                </div>
                <div className="text-[13.5px] text-navy font-medium truncate">
                  {ev.quote?.title || "Angebot"}
                </div>
                <div className="text-[11.5px] text-navy-dark/60 truncate">
                  {[ev.decider_name, ev.property_name].filter(Boolean).join(" · ")}
                </div>
                {ev.customer_note && (
                  <div className="text-[11.5px] text-navy-dark/70 italic mt-1 line-clamp-2">
                    &bdquo;{ev.customer_note}&ldquo;
                  </div>
                )}
              </div>
              <div className="text-right shrink-0">
                {typeof total === "number" && total > 0 && (
                  <div className="text-[12.5px] font-medium text-navy tabular-nums">{money(total, ev.quote?.currency || "EUR")}</div>
                )}
                <div className="text-[10.5px] text-navy-dark/50">
                  {formatDate(ev.created_at)}<br />
                  <span className="tabular-nums">{formatTime(ev.created_at)}</span>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
